import React from 'react';
import { AxiosResponse } from 'axios';
import { WEATHER_PAGE_SIZE } from 'ots-constants';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import {
  fetchRegionFeatured,
  fetchRegionLiftTicketsPricesByResort, fetchRegionMeta, fetchRegionRelated, fetchRegionRelatedNews,
} from 'api/region/static';
import { IRegionShort } from 'api/region/types';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { getRegionSlug } from 'util/memcache';

import { ILiftTicketsResponse } from 'components/region/LiftTicketTable/types';
import { LiftTicketTable } from 'components/region/LiftTicketTable';
import { SkipassesLiftTicketsInfo } from 'components/region/SkipassesLiftTicketsInfo';
import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import { Hero400Error } from 'components/400';
import { IRelatedNews } from 'api/resort/types';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';

export type RegionLiftTicketsPageProps = {
  resortPricesList: ILiftTicketsResponse,
  numResorts: number,
  pathInfo: IRegionShort,
  relatedNews: IRelatedNews | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-lift-tickets-page';
export const TEMPLATE_ID = '2.1.6';
export const TEMPLATE_NAME = 'Region Lift Tickets';

export default function RegionLiftTicketsPage({
  resortPricesList,
  pathInfo, numResorts,
  relatedNews,
}: RegionLiftTicketsPageProps) {
  useRegionLayoutBubble({});
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  if (!resortPricesList) {
    return <Hero400Error light />;
  }

  const seoText = pathInfo.isLogical ? m('region.liftTickets.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  }) : m('region.liftTickets.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  });

  const seoTitle = pathInfo.isLogical ? t('region.liftTickets.logicSeoTitle',
    { region: pathInfo.title }) : t('region.liftTickets.seoTitle', { region: pathInfo.title });

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={seoTitle} />
          <BlockContent>
            {seoText}
          </BlockContent>
        </BoxWithShadow>
        <BoxWithShadow>
          <BlockHeader title={t('region.liftTickets.skiPasses', { region: pathInfo.title })} />
          <BlockContent>
            {!!resortPricesList.pagination?.count && (
              <LiftTicketTable
                regionId={pathInfo.uuid}
                resortsPricesPreloaded={resortPricesList}
                isFresh
                hasMore={resortPricesList.pagination?.count > resortPricesList.pagination?.limit}
              />
            )}
          </BlockContent>
        </BoxWithShadow>
        {!resortPricesList.pagination?.count && (
          <BoxWithShadow>
            <BlockContent>
              <strong>
                {t('region.closingDates.noResorts', { region: pathInfo.title })}
              </strong>
            </BlockContent>
          </BoxWithShadow>
        )}
        <SkipassesLiftTicketsInfo
          header={t('region.liftTickets.passSeoInfoTitle', { region: pathInfo.title })}
          seoText={t('region.liftTickets.passSeoInfoText', { region: pathInfo.title })}
        />
        <PlanYourTrip
          region={pathInfo}
          hasSkiRental={false}
          hasCarRental={false}
          hasFlights={false}
          isPartner={false}
        />
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd isRegion id={pathInfo.uuid} hideWeSkiOnMobile />
      </Side>
    </>
  );
}

export const fetchProps = async ({ region }: { region: string }): Promise<RegionLiftTicketsPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);

  return {
    type: 'region',
    route: 'lift-tickets',
    resortPricesList: await fetchRegionLiftTicketsPricesByResort(uuid, 0, WEATHER_PAGE_SIZE),
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    featured,
    relatedNews: await fetchRegionRelatedNews(uuid),

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.TRIP_PLANNING,
      link: CurrentLink.LIFT_TICKETS,
    },
    adTKey: 'metaregion lift-tickets',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.liftTickets.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.lifttickets.description',
      title: 'MetaRegion.lifttickets.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionLiftTicketsPageStaticProps = wrapStaticProps(fetchProps);
export const getRegionLiftTicketsPageServerSideProps = wrapServerSideProps(fetchProps);
